import { useCallback, useEffect, useMemo, useState } from 'react'
import { ROW_DEFINITIONS as ROW_DEFINITIONS_EXTERNAL } from './rowDefinitions'
import { IS_DEVELOPMENT_ENVIRONMENT } from '../../../../../utils/constant'

const shouldLogF29Projection = () => IS_DEVELOPMENT_ENVIRONMENT

const getDefaultMonth = () => {
  const date = new Date()
  date.setMonth(date.getMonth() - 1)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  return `${year}-${month}`
}

const buildDataMap = (rows = []) =>
  rows.reduce((map, row) => {
    if (row?.codDocument) {
      map.set(String(row.codDocument), row)
    }
    return map
  }, new Map())

const getSectionSource = (row = {}, section) => {
  if (section === 'total') return row.total || {}
  return row[section] || {}
}

const collectRowMetrics = ({ row, section, montoField, pagoField }) => {
  const source = getSectionSource(row, section)
  const isDocumentSection = section === 'ventas' || section === 'compras'
  return {
    quantity: isDocumentSection ? Number(source.cantidadDocumentos ?? 0) : 0,
    amount: getAmountWithExento(source, montoField),
    payable: pagoField === null ? null : safeNumber(source[pagoField ?? montoField]),
  }
}

const sumMetricsByCodes = (codes = [], dataMap, options) =>
  codes.reduce(
    (acc, code) => {
      const row = dataMap.get(code)
      if (!row) return acc
      const metrics = collectRowMetrics({ row, ...options })
      return {
        quantity: acc.quantity + metrics.quantity,
        amount: acc.amount + metrics.amount,
        payable: acc.payable + metrics.payable,
      }
    },
    { quantity: 0, amount: 0, payable: 0 }
  )

const aggregateByDefinition = (definition, dataMap) => {
  const {
    codes = [],
    section = 'ventas',
    montoField = 'exento',
    pagoField = 'iva',
    codeLabel,
    summaryGroup,
    quantityLabel,
    montoLabel,
    summaryFactor = 1,
  } = definition

  const metrics = sumMetricsByCodes(codes, dataMap, { section, montoField, pagoField })

  return {
    key: definition.key,
    concepto: definition.title,
    codigo: (codeLabel ?? (codes.length ? codes.join('-') : '')) || '-',
    cantidadDocumentos: quantityLabel ?? metrics.quantity,
    monto: montoLabel ?? metrics.amount,
    montoAPagar: metrics.payable,
    summaryGroup,
    summaryFactor,
    section,
  }
}

const safeNumber = (value) => {
  const number = Number(value)
  return Number.isFinite(number) ? number : 0
}

const getAmountWithExento = (source = {}, field) => {
  const primary = safeNumber(source[field])
  if (field !== 'neto') return primary
  const exento = safeNumber(source.exento)
  return primary + (exento > 0 ? exento : 0)
}

const parsePercentageValue = (value) => {
  if (value === null || value === undefined) return null
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value > 1 ? value / 100 : value
  }
  if (typeof value === 'string') {
    const cleaned = value.replace('%', '').replace(',', '.').trim()
    if (!cleaned) return null
    const numeric = Number(cleaned)
    if (Number.isFinite(numeric)) {
      return numeric > 1 ? numeric / 100 : numeric
    }
  }
  return null
}

const extractValueByKeywords = (obj, keywords) => {
  if (!obj || typeof obj !== 'object') return null
  for (const [key, value] of Object.entries(obj)) {
    const lowerKey = key.toLowerCase()
    if (keywords.some((keyword) => lowerKey.includes(keyword))) {
      const parsed = parsePercentageValue(value)
      if (parsed !== null) return parsed
    }
  }

  for (const value of Object.values(obj)) {
    if (value && typeof value === 'object') {
      const nested = extractValueByKeywords(value, keywords)
      if (nested !== null) return nested
    }
  }
  return null
}

const extractPpmRateFromData = (ppmData) => {
  if (!ppmData) return null
  const keywords = ['porcentaje', 'percentage', 'percent', 'ppm', 'rate']
  return extractValueByKeywords(ppmData, keywords)
}

const PPM_POSITIVE_CODES = ['33', '34', '39', '48', '56']
const PPM_NEGATIVE_CODES = ['61', '112']

const getVentasAmountForPpm = (row) => {
  const ventas = row?.ventas || {}
  return getAmountWithExento(ventas, 'neto')
}

const calculatePpmBase = (dataMap) => {
  const sumAmounts = (codes, absolute = false) =>
    codes.reduce((acc, code) => {
      const row = dataMap.get(code)
      const amount = getVentasAmountForPpm(row)
      return acc + (absolute ? Math.abs(amount) : amount)
    }, 0)

  const positive = sumAmounts(PPM_POSITIVE_CODES)
  const negative = sumAmounts(PPM_NEGATIVE_CODES, true)

  return positive - negative
}

const calculatePpmMonto = (base, rate) => {
  const numericBase = safeNumber(base)
  if (typeof rate !== 'number' || !Number.isFinite(rate)) return 0
  return Math.round(numericBase * rate)
}

const ROW_DEFINITIONS = ROW_DEFINITIONS_EXTERNAL

const buildRows = (dataMap) => {
  const rows = []

  ROW_DEFINITIONS.forEach((definition) => {
    if (definition.resolver) {
      const resolved = definition.resolver({ dataMap, rows, definition })
      if (resolved) {
        rows.push({
          ...resolved,
          summaryGroup: resolved.summaryGroup ?? definition.summaryGroup,
          cantidadDocumentos:
            definition.quantityLabel !== undefined ? definition.quantityLabel : resolved.cantidadDocumentos,
          monto: definition.montoLabel !== undefined ? definition.montoLabel : resolved.monto,
          summaryFactor: definition.summaryFactor ?? resolved.summaryFactor ?? 1,
        })
      }
      return
    }
    rows.push(aggregateByDefinition(definition, dataMap))
  })

  const rowsByKey = new Map(rows.map((row) => [row.key, row]))
  const ppmRowIndex = rows.findIndex((row) => row.key === 'ppm')
  let ppmBase = 0
  let ppmRateDetected = null
  if (ppmRowIndex !== -1) {
    ppmBase = calculatePpmBase(dataMap)
    ppmRateDetected = extractPpmRateFromData(dataMap.get('PPM'))
    const appliedRate = ppmRateDetected ?? 0
    rows[ppmRowIndex] = {
      ...rows[ppmRowIndex],
      monto: ppmRateDetected !== null ? `${(ppmRateDetected * 100).toFixed(2)}%` : '-',
      montoAPagar: calculatePpmMonto(ppmBase, appliedRate),
    }
    rowsByKey.set('ppm', rows[ppmRowIndex])
  }

  const sumRowValues = (keys = []) =>
    keys.reduce((acc, key) => acc + safeNumber(rowsByKey.get(key)?.montoAPagar), 0)

  const debitKeys = [
    'ventas_factura',
    'ventas_boletas',
    'ventas_pago_electronico',
    'nd_afectas',
    'nc_afectas',
    'liquidacion_factura',
  ]
  const creditKeys = [
    'compras_afectas',
    'compras_liquidacion',
    'compras_facturas',
    'compras_nd_afectas',
    'compras_nc_afectas',
    'importaciones',
  ]
  const retencionKeys = ['retencion_145', 'retencion_3', 'impuesto_unico', 'impuesto_unico_3', 'ppm']

  const netIvaBalance = sumRowValues(debitKeys) - sumRowValues(creditKeys) + safeNumber(rowsByKey.get('remanente_anterior')?.montoAPagar)
  const retencionesTotal = sumRowValues(retencionKeys)
  const ivaServiciosValue = safeNumber(rowsByKey.get('iva_servicios')?.montoAPagar)
  const devolucionesValue = safeNumber(rowsByKey.get('iva_servicios_nc')?.montoAPagar) + safeNumber(rowsByKey.get('devolucion_export')?.montoAPagar)

  const totalMontoPagar =
    netIvaBalance < 0
      ? retencionesTotal + ivaServiciosValue
      : netIvaBalance + retencionesTotal + ivaServiciosValue + devolucionesValue

  const totalRow = {
    key: 'total_f29',
    concepto: 'TOTAL F29 A PAGAR',
    codigo: '-',
    cantidadDocumentos: '-',
    monto: '-',
    montoAPagar: totalMontoPagar,
    section: 'totales',
  }
  rows.push(totalRow)
  rowsByKey.set('total_f29', totalRow)

  return { rows, ppmBase, ppmRateDetected }
}

const SUMMARY_LAYOUT = [
  { key: 'total_iva_debito', label: 'TOTAL IVA DÉBITO', type: 'group', group: 'total_iva_debito' },
  { key: 'total_iva_credito', label: 'TOTAL IVA CRÉDITO', type: 'group', group: 'total_iva_credito', multiplier: -1 },
  { key: 'remanente_anterior', label: 'REMANENTE IVA MES ANTERIOR', type: 'row', rowKey: 'remanente_anterior' },
  { key: 'devolucion_export', label: 'DEVOLUCIÓN IVA EXPORTACIONES', type: 'row', rowKey: 'devolucion_export' },
  { key: 'retencion_boleta', label: 'RETENCIÓN BOLETA', type: 'group', group: 'retencion_boleta' },
  { key: 'impuesto_unico', label: 'IMPUESTO ÚNICO TRABAJADORES', type: 'row', rowKey: 'impuesto_unico' },
  { key: 'ppm', label: 'PPM', type: 'row', rowKey: 'ppm' },
  { key: 'iva_internacional', label: 'IVA FACTURAS INTERNACIONALES', type: 'group', group: 'iva_facturas_internacionales' },
  { key: 'retencion_prestamo', label: 'RETENCIÓN PRÉSTAMO 3%', type: 'row', rowKey: 'impuesto_unico_3' },
  { key: 'total_pagar', label: 'TOTAL A PAGAR', type: 'row', rowKey: 'total_f29' },
]

const ALWAYS_NEGATIVE_SUMMARY_KEYS = new Set(['total_iva_credito', 'remanente_anterior', 'devolucion_export'])

const buildSummary = (rows = []) => {
  if (!rows.length) return []

  const rowMap = rows.reduce((acc, row) => {
    acc[row.key] = row
    return acc
  }, {})

  const groupTotals = rows.reduce((acc, row) => {
    if (!row.summaryGroup) return acc
    const factor = row.summaryFactor ?? 1
    acc[row.summaryGroup] = (acc[row.summaryGroup] || 0) + safeNumber(row.montoAPagar) * factor
    return acc
  }, {})

  const summaryItems = SUMMARY_LAYOUT.map((item) => {
    const baseValue =
      item.type === 'group'
        ? groupTotals[item.group] ?? 0
        : safeNumber(rowMap[item.rowKey]?.montoAPagar)
    const adjustedValue = (item.multiplier ? baseValue * item.multiplier : baseValue) || 0
    const normalizedValue = ALWAYS_NEGATIVE_SUMMARY_KEYS.has(item.key)
      ? -Math.abs(adjustedValue)
      : adjustedValue

    return { key: item.key, label: item.label, value: normalizedValue }
  })

  const getValue = (key) => safeNumber(summaryItems.find((item) => item.key === key)?.value)
  const sumKeys = (keys = []) => keys.reduce((acc, key) => acc + getValue(key), 0)

  const ivaBlock = sumKeys(['total_iva_debito', 'total_iva_credito', 'remanente_anterior'])
  const baseRange = sumKeys([
    'total_iva_debito',
    'total_iva_credito',
    'remanente_anterior',
    'devolucion_export',
    'retencion_boleta',
    'impuesto_unico',
    'ppm',
    'iva_internacional',
  ])
  const alternativeRange = sumKeys(['retencion_boleta', 'impuesto_unico', 'ppm', 'iva_internacional'])
  const retencionPrestamo = getValue('retencion_prestamo')

  const totalPagarValue = (ivaBlock < 0 ? alternativeRange : baseRange) + retencionPrestamo
  if (shouldLogF29Projection()) {
    console.groupCollapsed('[F29 projection] summary calculation')
    console.table(summaryItems)
    console.log('ivaBlock:', ivaBlock)
    console.log('baseRange:', baseRange)
    console.log('alternativeRange:', alternativeRange)
    console.log('retencionPrestamo:', retencionPrestamo)
    console.log('totalPagarValue:', totalPagarValue)
    console.groupEnd()
  }

  return summaryItems.map((item) =>
    item.key === 'total_pagar' ? { ...item, value: totalPagarValue } : item
  )
}

const DEFAULT_PPM_RATE = 0.25 

export const useResumenF29 = ({ businessId, businessAPI, ui }) => {
  const defaultMonth = useMemo(() => getDefaultMonth(), [])
  const setDialog = ui?.setDialog
  const [rows, setRows] = useState([])
  const [month, setMonth] = useState(defaultMonth)
  const [isLoading, setIsLoading] = useState(false)
  const [ppmBase, setPpmBase] = useState(0)
  const [ppmInputValue, setPpmInputValue] = useState('')

  const summaryItems = useMemo(() => buildSummary(rows), [rows])
  const totalSummaryValue = useMemo(
    () => safeNumber(summaryItems.find((item) => item.key === 'total_pagar')?.value),
    [summaryItems]
  )
  const rowsForDisplay = useMemo(
    () =>
      rows.map((row) =>
        row.key === 'total_f29'
          ? { ...row, montoAPagar: totalSummaryValue }
          : row
      ),
    [rows, totalSummaryValue]
  )

  const applyPpmRateToRows = useCallback((targetRows, base, rate) => {
    if (!Array.isArray(targetRows)) return targetRows
    const hasValidRate = typeof rate === 'number' && Number.isFinite(rate)
    return targetRows.map((row) => {
      if (row.key !== 'ppm') return row
      return {
        ...row,
        monto: hasValidRate ? `${(rate * 100).toFixed(2)}%` : '-',
        montoAPagar: hasValidRate ? calculatePpmMonto(base, rate) : 0,
      }
    })
  }, [])

  const fetchData = useCallback(
    async (targetMonth) => {
      if (!businessId) return
      const monthToQuery = targetMonth ?? defaultMonth
      setIsLoading(true)
      try {
        const { data } = await businessAPI.getResumenF29({
          businessId,
          month: monthToQuery || undefined,
        })
        const dataMap = buildDataMap(Array.isArray(data) ? data : [])
        const { rows: builtRows, ppmBase: calculatedBase, ppmRateDetected } = buildRows(dataMap)
        const initialRate = typeof ppmRateDetected === 'number' ? ppmRateDetected : DEFAULT_PPM_RATE / 100
        const savedRate = localStorage.getItem('ppm-rate')
        const resolvedInputValue = savedRate ?? (initialRate * 100).toFixed(2)
        const resolvedRate = savedRate ? Number(savedRate) / 100 : initialRate

        if (shouldLogF29Projection()) {
          console.groupCollapsed('[F29 projection] fetched data')
          console.log('businessId:', businessId)
          console.log('month:', monthToQuery)
          console.log('raw data:', data)
          console.log('dataMap keys:', Array.from(dataMap.keys()))
          console.table(builtRows)
          console.groupEnd()

          console.groupCollapsed('[F29 projection] PPM calculation')
          console.log('ppmBase:', calculatedBase)
          console.log('ppmRateDetected:', ppmRateDetected)
          console.log('savedRate:', savedRate)
          console.log('resolvedInputValue:', resolvedInputValue)
          console.log('resolvedRate:', resolvedRate)
          console.log('ppmMonto:', calculatePpmMonto(calculatedBase, resolvedRate))
          console.groupEnd()
        }

        setPpmBase(calculatedBase)
        setPpmInputValue(resolvedInputValue)
        setRows(applyPpmRateToRows(builtRows, calculatedBase, resolvedRate))
      } catch (error) {
        console.error('Error al obtener el resumen F29', error)
        setDialog?.({
          open: true,
          title: 'Error',
          body: error.response?.data?.message || 'No se pudo obtener el resumen F29',
          btnText: 'Cerrar',
        })
      } finally {
        setIsLoading(false)
      }
    },
    [applyPpmRateToRows, businessAPI, businessId, defaultMonth, setDialog]
  )

  useEffect(() => {
    if (ppmInputValue === '' || ppmInputValue == null) return
    localStorage.setItem('ppm-rate', ppmInputValue)
  }, [ppmInputValue])

  useEffect(() => {
    if (businessId) {
      setMonth(defaultMonth)
      fetchData(defaultMonth)
    }
  }, [businessId, defaultMonth, fetchData])

  const handlePpmInputChange = (event) => {
    const { value } = event.target
    setPpmInputValue(value)
    const normalized = value.replace(',', '.')
    if (normalized.trim() === '') {
      setRows((prevRows) => applyPpmRateToRows(prevRows, ppmBase, null))
      return
    }
    const numeric = Number(normalized)
    if (Number.isFinite(numeric)) {
      const decimalRate = numeric / 100
      setRows((prevRows) => applyPpmRateToRows(prevRows, ppmBase, decimalRate))
    }
  }

  const handleSearch = () => fetchData(month || defaultMonth)
  const handleReset = () => {
    setMonth(defaultMonth)
    fetchData(defaultMonth)
  }

  const ppmTotal = useMemo(() => {
    const ppmRow = rows.find(r => r.key === 'ppm')
    return safeNumber(ppmRow?.montoAPagar)
  }, [rows])

  return {
    rows: rowsForDisplay,
    summaryItems,
    month,
    setMonth,
    isLoading,
    ppmInputValue,
    handlePpmInputChange,
    handleSearch,
    handleReset,
    ppmTotal,
  }
}
